import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { SmallText, BodyText } from '../brand/BrandTypography';
import { getCycleSteps } from '../../services/relationshipToolsService';

/**
 * InteractiveCycleVisual - Animated infinity cycle diagram
 * 
 * Shows the EFT negative relationship cycle with interactive elements.
 * Users can tap on each part of the cycle to learn more.
 * Fetches cycle step data from the database.
 */

// Fallback data when database is unavailable
const FALLBACK_CYCLE_STEPS = [
    {
        step_id: 'unmet-needs',
        label: 'Unmet Attachment Needs',
        position: { x: 50, y: 15 },
        color: '#FFBB98',
        description: 'The deepest desires for how you want to feel in your relationship.',
        examples: ['To be seen', 'To be understood', 'To feel connected', 'To feel accepted'],
        detail: 'These are triggered when your partner\'s behavior feels threatening to your sense of safety or connection.'
    },
    {
        step_id: 'primary-emotion',
        label: 'Primary Emotion',
        sublabel: '(Below the Surface)',
        position: { x: 20, y: 40 },
        color: '#7D8E95',
        description: 'Your gut reaction when attachment needs are triggered.',
        examples: ['Loneliness', 'Fear of abandonment', 'Feeling unseen', 'Insecurity'],
        detail: 'This is often too vulnerable to share, or you don\'t trust it will be handled gently.'
    },
    {
        step_id: 'secondary-emotion',
        label: 'Secondary Emotion',
        sublabel: '(Surface Behavior)',
        position: { x: 80, y: 40 },
        color: '#7D8E95',
        description: 'The emotion you show to mask the vulnerable one.',
        examples: ['Anger', 'Coldness', 'Criticism', 'Withdrawal', 'Sarcasm'],
        detail: 'This protects the softer feeling because it feels safer than being vulnerable.'
    },
    {
        step_id: 'attributions',
        label: 'Attributions',
        position: { x: 20, y: 70 },
        color: '#FFBB98',
        description: 'The stories you tell yourself about your partner\'s actions.',
        examples: ['"They don\'t care"', '"They never listen"', '"It\'s never enough"', '"They don\'t respect me"'],
        detail: 'These stories can justify your reactions and damage the relationship — but they\'re often not accurate.'
    },
    {
        step_id: 'behavior',
        label: 'Behavior',
        sublabel: '(Triggers Partner)',
        position: { x: 80, y: 70 },
        color: '#1D6B6B',
        description: 'The outward actions taken for self-protection.',
        examples: ['Pursuing: demanding, criticizing, pleading', 'Withdrawing: shutting down, avoiding, going silent'],
        detail: 'This behavior then triggers your partner\'s attachment needs, and the cycle continues.'
    }
];

// Transform database format to component format
const transformStep = (dbStep) => ({
    id: dbStep.step_id,
    label: dbStep.label,
    sublabel: dbStep.sublabel || null,
    position: dbStep.position,
    color: dbStep.color,
    description: dbStep.description,
    examples: dbStep.examples,
    detail: dbStep.detail
});

// Custom hook for fetching cycle steps
export const useCycleStepsData = () => {
    const [cycleSteps, setCycleSteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getCycleSteps();
                setCycleSteps(data.map(transformStep));
                setLoading(false);
            } catch (err) {
                console.error('Error fetching cycle steps:', err);
                setCycleSteps(FALLBACK_CYCLE_STEPS.map(transformStep));
                setError(err.message);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { cycleSteps, loading, error };
};

// Legacy export for backwards compatibility
export const cycleSteps = FALLBACK_CYCLE_STEPS.map(transformStep);

export const InteractiveCycleVisual = ({ className }) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const [animationPhase, setAnimationPhase] = useState(0);
    const { cycleSteps, loading } = useCycleStepsData();

    // Animate through the cycle
    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationPhase(prev => (prev + 1) % 5);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className={cn('flex items-center justify-center py-8', className)}>
                <div className="w-8 h-8 rounded-full border-2 border-[#7D8E95] border-t-transparent animate-spin" />
            </div>
        );
    }

    return (
        <div className={cn('relative', className)}>
            {/* SVG Infinity Cycle */}
            <div className="relative w-full aspect-[4/3] bg-[var(--orenda-soft-white)] rounded-3xl border border-[var(--orenda-border)] overflow-hidden">
                <svg 
                    viewBox="0 0 400 300" 
                    className="w-full h-full"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.05))' }}
                >
                    {/* Infinity path */}
                    <motion.path
                        d="M200,150 C200,100 120,80 80,120 C40,160 40,200 80,240 C120,280 200,200 200,150 C200,100 280,80 320,120 C360,160 360,200 320,240 C280,280 200,200 200,150"
                        fill="none"
                        stroke="url(#cycleGradient)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 3, ease: "easeInOut" }}
                    />
                    
                    {/* Animated dot following the path */}
                    <motion.circle
                        r="6"
                        fill="#1D6B6B"
                        initial={{ offsetDistance: "0%" }}
                        animate={{ offsetDistance: "100%" }}
                        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                        style={{ offsetPath: `path("M200,150 C200,100 120,80 80,120 C40,160 40,200 80,240 C120,280 200,200 200,150 C200,100 280,80 320,120 C360,160 360,200 320,240 C280,280 200,200 200,150")` }}
                    />
                    
                    {/* Gradient definition */}
                    <defs>
                        <linearGradient id="cycleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#FFBB98" />
                            <stop offset="25%" stopColor="#7D8E95" />
                            <stop offset="50%" stopColor="#7D8E95" />
                            <stop offset="75%" stopColor="#FFBB98" />
                            <stop offset="100%" stopColor="#1D6B6B" />
                        </linearGradient>
                    </defs>
                    
                    {/* Labels */}
                    <text x="200" y="40" textAnchor="middle" className="text-xs fill-[#FFBB98] font-medium">
                        Unmet Needs
                    </text>
                    <text x="60" y="120" textAnchor="middle" className="text-xs fill-[#7D8E95] font-medium">
                        Primary
                    </text>
                    <text x="60" y="135" textAnchor="middle" className="text-[10px] fill-[#7D8E95]">
                        (underneath)
                    </text>
                    <text x="340" y="120" textAnchor="middle" className="text-xs fill-[#7D8E95] font-medium">
                        Secondary
                    </text>
                    <text x="340" y="135" textAnchor="middle" className="text-[10px] fill-[#7D8E95]">
                        (surface)
                    </text>
                    <text x="60" y="220" textAnchor="middle" className="text-xs fill-[#FFBB98] font-medium">
                        Stories
                    </text>
                    <text x="340" y="220" textAnchor="middle" className="text-xs fill-[#1D6B6B] font-medium">
                        Behavior
                    </text>
                    
                    {/* Partner labels */}
                    <text x="110" y="270" textAnchor="middle" className="text-[10px] fill-[var(--orenda-text-muted)]">
                        Partner A
                    </text>
                    <text x="290" y="270" textAnchor="middle" className="text-[10px] fill-[var(--orenda-text-muted)]">
                        Partner B
                    </text>
                    
                    {/* Arrows showing cycle direction */}
                    <path d="M180,150 L195,145 M195,155 L180,150" stroke="#7D8E95" strokeWidth="2" fill="none" />
                    <path d="M220,150 L205,145 M205,155 L220,150" stroke="#7D8E95" strokeWidth="2" fill="none" />
                </svg>
                
                {/* Interactive hotspots */}
                {cycleSteps.map((step, index) => (
                    <motion.button
                        key={step.id}
                        onClick={() => setSelectedStep(step)}
                        className={cn(
                            'absolute w-10 h-10 rounded-full',
                            'flex items-center justify-center',
                            'transition-all duration-300',
                            'hover:scale-110',
                            animationPhase === index ? 'ring-2 ring-offset-2' : ''
                        )}
                        style={{
                            left: `${step.position.x}%`,
                            top: `${step.position.y}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: `${step.color}20`,
                            borderColor: step.color,
                            ringColor: step.color
                        }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        data-testid={`cycle-step-${step.id}`}
                    >
                        <span 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: step.color }}
                        />
                    </motion.button>
                ))}
            </div>

            {/* Step Detail Modal */}
            <AnimatePresence>
                {selectedStep && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="mt-4 p-5 rounded-2xl bg-[var(--orenda-soft-white)] border border-[var(--orenda-border)] shadow-lg"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div 
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: selectedStep.color }}
                                />
                                <div>
                                    <BodyText className="font-semibold text-[var(--orenda-text-primary)]">
                                        {selectedStep.label}
                                    </BodyText>
                                    {selectedStep.sublabel && (
                                        <SmallText className="text-[var(--orenda-text-muted)]">
                                            {selectedStep.sublabel}
                                        </SmallText>
                                    )}
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedStep(null)}
                                className="p-1.5 rounded-full hover:bg-[var(--orenda-border)]/50 transition-colors"
                            >
                                <X className="w-4 h-4 text-[var(--orenda-text-muted)]" />
                            </button>
                        </div>
                        
                        <BodyText className="text-[var(--orenda-text-secondary)] mb-3 leading-relaxed">
                            {selectedStep.description}
                        </BodyText>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                            {selectedStep.examples.map((example, i) => (
                                <span 
                                    key={i}
                                    className="px-2.5 py-1 rounded-full text-xs"
                                    style={{ 
                                        backgroundColor: `${selectedStep.color}15`,
                                        color: selectedStep.color
                                    }}
                                >
                                    {example}
                                </span>
                            ))}
                        </div>
                        
                        <SmallText className="text-[var(--orenda-text-muted)] italic">
                            {selectedStep.detail}
                        </SmallText>
                    </motion.div>
                )}
            </AnimatePresence>
            
            <SmallText className="text-center text-[var(--orenda-text-muted)] mt-4">
                Tap on any part of the cycle to learn more
            </SmallText>
        </div>
    );
};

export default InteractiveCycleVisual;
